import React from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { RubricGradingType } from 'libs/end-to-end/src/types/rubrics/rubric';
import { IconWrittenResponseRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconWrittenResponseRubric28';
import { IconInterviewRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconInterviewRubric28';
import { IconResumeRubric28Svg } from 'apps/sigma2-ui/src/components/svg/IconResumeRubric28';
import { TertiaryButton } from 'apps/sigma2-ui/src/components/TertiaryButton';
import { IconSaveSvg } from 'apps/sigma2-ui/src/components/svg/IconSave';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { IconUnpublishSvg } from 'apps/sigma2-ui/src/components/svg/IconUnpublish';
import { PageHeader } from 'apps/sigma2-ui/src/components/page-header/PageHeader';
import { PageHeaderBackButton } from 'apps/sigma2-ui/src/components/page-header/PageHeaderBackButton';
import { PageHeaderBadge } from 'apps/sigma2-ui/src/components/page-header/PageHeaderBadge';
import { PageHeaderStatusPill } from 'apps/sigma2-ui/src/components/page-header/PageHeaderStatusPill';

const EditRubricGradingTypeIconAndLabel: React.FC<{
  gradingType: RubricGradingType;
}> = ({ gradingType }) => {
  const language = useLanguage();
  return (
    <Spacing direction="horizontal" size="sm" className="text-indigo-900">
      {gradingType === 'interview' && <IconInterviewRubric28Svg />}
      {gradingType === 'questions' && <IconWrittenResponseRubric28Svg />}
      {gradingType === 'resume' && <IconResumeRubric28Svg />}
      <S2Typography
        variant="body-6-bold"
        textColorVariant="indigo-900"
        translatedContent={
          gradingType === 'interview'
            ? language.interviewRubricShortTitle()
            : gradingType === 'questions'
              ? language.writtenResponseRubricShortTitle()
              : language.resumeRubricShortTitle()
        }
      />
    </Spacing>
  );
};

const EditRubricSaveDraftButton: React.FC<{
  onSaveDraft: () => void;
  disabled: boolean;
}> = ({ onSaveDraft, disabled }) => {
  const language = useLanguage();
  return (
    <TertiaryButton
      label={language.saveAsDraft()}
      icon={<IconSaveSvg />}
      disabled={disabled}
      behavior={{
        type: 'onClick',
        onClick: onSaveDraft,
      }}
    />
  );
};

const EditRubricUnpublishButton: React.FC<{
  onUnpublish: () => void;
  disabled: boolean;
}> = ({ onUnpublish, disabled }) => {
  const language = useLanguage();
  return (
    <TertiaryButton
      label={language.unpublish()}
      icon={<IconUnpublishSvg />}
      disabled={disabled}
      behavior={{
        type: 'onClick',
        onClick: onUnpublish,
      }}
    />
  );
};

const EditRubricRestoreButton: React.FC<{
  onRestore: () => void;
  disabled: boolean;
}> = ({ onRestore, disabled }) => {
  const language = useLanguage();
  return (
    <PrimaryButtonLightBg
      translatedContent={language.restore()}
      onClick={onRestore}
      disabled={disabled}
    />
  );
};

const EditRubricActionButtons: React.FC<{
  isPublished: boolean;
  isArchiveMode: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onRestore: () => void;
  disableSave: boolean;
}> = ({
  isPublished,
  isArchiveMode,
  onSaveDraft,
  onPublish,
  onUnpublish,
  onRestore,
  disableSave,
}) => {
  const language = useLanguage();
  return (
    <Spacing
      direction="horizontal"
      size="md"
      className="items-center flex-wrap"
    >
      {isArchiveMode && (
        <EditRubricRestoreButton onRestore={onRestore} disabled={false} />
      )}
      {!isArchiveMode && !isPublished && (
        <EditRubricSaveDraftButton
          onSaveDraft={onSaveDraft}
          disabled={disableSave}
        />
      )}
      {!isArchiveMode && !isPublished && (
        <PrimaryButtonLightBg
          translatedContent={language.publish()}
          onClick={onPublish}
          disabled={disableSave}
        />
      )}
      {!isArchiveMode && isPublished && (
        <EditRubricUnpublishButton
          onUnpublish={onUnpublish}
          disabled={disableSave}
        />
      )}
      {!isArchiveMode && isPublished && (
        <PrimaryButtonLightBg
          translatedContent={language.saveAndPublish()}
          onClick={onPublish}
          disabled={disableSave}
        />
      )}
    </Spacing>
  );
};

const EditRubricLiveIndicator: React.FC = () => {
  const language = useLanguage();
  return (
    <PageHeaderStatusPill
      translatedContent={language.live()}
      className="px-0 py-0"
      dotClassName="w-3 h-3 bg-green-500"
      variant="body-7-bold"
      textColorVariant="green-600"
    />
  );
};

export const EditRubricViewHeader: React.FC<{
  name: string;
  gradingType: RubricGradingType;
  createdAt?: Date;
  updatedAt?: Date;
  isPublished: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onRestore: () => void;
  disableSave: boolean;
  isReadOnlyMode: boolean;
  isArchiveMode: boolean;
  canManage: boolean;
}> = ({
  name,
  gradingType,
  createdAt,
  updatedAt,
  isPublished,
  onSaveDraft,
  onPublish,
  onUnpublish,
  onRestore,
  disableSave,
  isReadOnlyMode,
  isArchiveMode,
  canManage,
}) => {
  const language = useLanguage();
  return (
    <PageHeader
      left={
        <>
          <PageHeaderBackButton
            behavior={{
              type: 'href',
              href: '/rubrics',
            }}
          />
          <div className="min-w-0 flex-1">
            <S2Typography
              variant="headline-4"
              textColorVariant="gray-1000"
              translatedContent={[
                {
                  text: name || language.newRubric(),
                  tooltip: name || language.newRubric(),
                },
              ]}
              dangerouslyOverrideClassNameFn={(cn) => `${cn} max-w-[400px]`}
              truncate
            />
          </div>
        </>
      }
      center={
        <>
          <EditRubricGradingTypeIconAndLabel gradingType={gradingType} />
          <Spacing
            size="sm"
            direction="horizontal"
            className="items-center flex-wrap"
          >
            <PageHeaderBadge
              translatedContent={language.createdAtOrDefault({
                date: createdAt,
              })}
            />
            <PageHeaderBadge
              translatedContent={language.updatedAtOrDefault({
                date: updatedAt,
              })}
            />
            {isPublished && <EditRubricLiveIndicator />}
          </Spacing>
        </>
      }
      right={
        (!isReadOnlyMode || isArchiveMode) && canManage ? (
          <EditRubricActionButtons
            isPublished={isPublished}
            isArchiveMode={isArchiveMode}
            onSaveDraft={onSaveDraft}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
            onRestore={onRestore}
            disableSave={disableSave}
          />
        ) : null
      }
    />
  );
};
